import React, { useEffect, useState } from "react";
import { FaEdit, FaGripVertical, FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { getCategoriesAPI, deleteCategoryAPI, updateCategoryPositionsAPI } from '../../utils/api';
import Sidebar from "../Sidebar/Sidebar";
import { BASE_URL } from '../../utils/configConstants';
import SizeChartModal from './components/SizeChartModal';
import "./Categories.css";

// Sortable Row Component
const SortableRow = ({ category, idx, startIdx, onRowClick, onActionClick, onSizeChartClick }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: category._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <tr
            ref={setNodeRef}
            style={{ ...style, cursor: "pointer" }}
            onClick={() => onRowClick(category)}
        >
            <td>{startIdx + idx + 1}</td>
            <td>
                <div className="drag-handle" {...attributes} {...listeners}>
                    <FaGripVertical />
                </div>
            </td>
            <td>{category.name || ''}</td>
            <td>
                <div className="category-image-cell">
                    {category.image ? (
                        <img
                            src={BASE_URL + category?.image}
                            alt={category.name || 'Category'}
                            className="category-table-image"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextSibling) {
                                    e.target.nextSibling.style.display = 'flex';
                                }
                            }}
                        />
                    ) : null}
                    {!category.image && (
                        <div className="category-no-image">
                            <span>No Image</span>
                        </div>
                    )}
                </div>
            </td>
            <td>
                {category.size_chart && category.size_chart.length > 0 ? (
                    <button
                        type="button"
                        className="size-chart-view-link"
                        onClick={(e) => onSizeChartClick(e, category.size_chart)}
                        aria-label="View Size Chart"
                    >
                        View
                    </button>
                ) : (
                    <span></span>
                )}
            </td>
            <td>
                <div className="categories-action-btn-row">
                    <button
                        type="button"
                        className="categories-action-btn categories-edit-btn"
                        title="Edit Category"
                        onClick={(e) => onActionClick(e, 'edit', category)}
                        aria-label="Edit"
                    >
                        <FaEdit />
                    </button>
                    <button
                        type="button"
                        className="categories-action-btn categories-delete-btn"
                        title="Delete Category"
                        onClick={(e) => onActionClick(e, 'delete', category)}
                        aria-label="Delete"
                    >
                        <MdDelete />
                    </button>
                </div>
            </td>
        </tr>
    );
};

const Categories = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [toast, setToast] = useState({ show: false, message: "", type: "" });
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [sortField, setSortField] = useState("name");
    const [sortOrder, setSortOrder] = useState("asc");
    const [isUpdatingPositions, setIsUpdatingPositions] = useState(false);
    const [showSizeChartModal, setShowSizeChartModal] = useState(false);
    const [selectedSizeChart, setSelectedSizeChart] = useState(null);

    const PAGE_LIMIT = 10;

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
    };

    // Handle drag end for position updates
    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const draggedCategory = categories.find(category => category._id === active.id);
            const targetCategory = categories.find(category => category._id === over.id);

            if (!draggedCategory || !targetCategory) return;

            const draggedPosition = draggedCategory.position;
            const targetPosition = targetCategory.position;

            // Create updated categories with new positions
            const updatedCategories = categories.map(category => {
                if (category._id === draggedCategory._id) {
                    return { ...category, position: targetPosition };
                } else if (category._id === targetCategory._id) {
                    return { ...category, position: draggedPosition };
                }
                return category;
            });

            // Sort by position to maintain order
            const sortedCategories = updatedCategories.sort((a, b) => a.position - b.position);
            setCategories(sortedCategories);

            // Update positions on server
            setIsUpdatingPositions(true);
            try {
                const positionsData = [
                    { category_id: draggedCategory._id, position: targetPosition },
                    { category_id: targetCategory._id, position: draggedPosition }
                ];
                console.log(positionsData);

                const response = await updateCategoryPositionsAPI({ positions: positionsData });

                if (response?.status === 200) {
                    // Update specific categories with response data if available
                    if (response?.data && Array.isArray(response.data)) {
                        const updatedCategories = categories.map(category => {
                            const updatedCategory = response.data.find(updated => updated._id === category._id);
                            if (updatedCategory) {
                                console.log('updatedCategory-=-==-=-=', updatedCategory);
                                return { ...category, position: updatedCategory.position };
                            }
                            return category;
                        });
                        console.log('updatedCategories-=-==-=-=', updatedCategories);
                        setCategories(updatedCategories);
                        // Ensure categories are sorted after update
                        const sortedCategories = updatedCategories.sort((a, b) => a.position - b.position);
                        setCategories(sortedCategories);
                    }
                } else {
                    // Revert local changes if server update fails
                    setCategories(categories);
                    showToast(response?.message || 'Failed to update positions', "error");
                }
            } catch (err) {
                // Revert local changes if server update fails
                setCategories(categories);
                console.error('Position update error:', err);
                showToast('Failed to update positions. Please try again.', "error");
            } finally {
                setIsUpdatingPositions(false);
            }
        }
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getCategoriesAPI({
                page,
                limit: PAGE_LIMIT,
                search: searchTerm.trim().toLowerCase() || "",
            })

            if (response?.status == 200) {
                const list = response.data
                const totalPagesFromApi = response?.pagination?.total_pages || 1;
                const currentPageFromApi = response?.pagination?.current_page || page;
                setCategories(list);
                setTotalPages(totalPagesFromApi);
                if (currentPageFromApi !== page) setPage(currentPageFromApi);
            } else {
                setError(response?.message || 'Failed to fetch categories');
            }
        } catch (err) {
            console.log('Fetch error:', err);
            setError('Failed to fetch categories. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
    }, [page, searchTerm]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    const totalEntries = categories.length;
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * PAGE_LIMIT;
    const endIdx = startIdx + categories.length;

    const handleEdit = (category) => {
        navigate(`/edit-category/${category._id}`, { state: { category } });
    };

    const handleAddNew = () => {
        navigate('/add-category');
    };


    const handleDeleteClick = (category) => {
        setCategoryToDelete(category);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        try {

            if (!categoryToDelete?._id) {
                showToast("Invalid category data", "error");
                return;
            }

            const reqData = {
                category_id: categoryToDelete._id
            };

            const response = await deleteCategoryAPI(reqData);

            if (response?.status === 200) {
                setShowDeleteModal(false);
                setCategoryToDelete(null);
                showToast(`Category "${categoryToDelete?.name}" deleted successfully!`, "success");
                await fetchCategories();
            } else {
                showToast(response?.message || 'Failed to delete category', "error");
            }
        } catch (err) {
            console.log('Delete error:', err);
            showToast('An error occurred. Please try again.', "error");
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteModal(false);
        setCategoryToDelete(null);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
    };

    const getPageItems = () => {
        const items = [];
        const current = Math.max(1, Math.min(page, totalPages));
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) items.push(i);
            return items;
        }

        items.push(1);
        const showLeftDots = current > 4;
        const showRightDots = current < totalPages - 3;

        if (showLeftDots) items.push('dots-left');

        const start = Math.max(2, current - 1);
        const end = Math.min(totalPages - 1, current + 1);
        for (let i = start; i <= end; i++) items.push(i);

        if (showRightDots) items.push('dots-right');

        items.push(totalPages);
        return items;
    };

    const handleRowClick = (category) => {
        navigate(`/product?categoryId=${category._id}`);
    };

    const handleActionClick = (e, action, category) => {
        e.stopPropagation();
        if (action === 'edit') {
            handleEdit(category);
        } else if (action === 'delete') {
            handleDeleteClick(category);
        }
    };

    const handleSizeChartClick = (e, sizeChart) => {
        e.stopPropagation();
        setSelectedSizeChart(sizeChart);
        setShowSizeChartModal(true);
    };

    const handleCloseSizeChartModal = () => {
        setShowSizeChartModal(false);
        setSelectedSizeChart(null);
    };


    return (
        <>
            <Toast toast={toast} />

            <div className="Categories-layout">
                <Sidebar />
                <div className="Categories-main-content">
                    <div className="Categories-header-row">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search by category name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Search categories"
                        />
                        <button
                            type="button"
                            className="insert-btn"
                            onClick={handleAddNew}
                            aria-label="Add Category"
                        >
                            <span>Add Category</span>
                        </button>
                    </div>

                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Loading categories...</p>
                        </div>
                    ) : error ? (
                        <div className="loading-container">
                            <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
                            <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
                        </div>
                    ) : totalEntries === 0 ? (
                        <div className="empty-state">
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📂</div>
                            <h3>No Categories Found</h3>
                            <p>There are no categories in the system yet.</p>
                            <p>Click "Add Category" to create the first one.</p>
                        </div>
                    ) : categories.length === 0 ? (
                        <div className="empty-state">
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔎</div>
                            <h3>No results</h3>
                            <p>We couldn't find any category matching "{searchTerm}".</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <table className="Categories-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th className="drag-handle-col">Drag</th>
                                                <th aria-label="Sort by Category Name">
                                                    Category Name
                                                </th>
                                                <th>Image</th>
                                                <th>Size Chart</th>
                                                <th className="actions-col">Actions</th>
                                            </tr>
                                        </thead>
                                        <SortableContext
                                            items={categories.map(cat => cat?._id)}
                                            strategy={verticalListSortingStrategy}
                                        >
                                            <tbody>
                                                {categories.map((category, idx) => (
                                                    <SortableRow
                                                        key={category?._id || `category-${idx}`}
                                                        category={category}
                                                        idx={idx}
                                                        startIdx={startIdx}
                                                        onRowClick={handleRowClick}
                                                        onActionClick={handleActionClick}
                                                        onSizeChartClick={handleSizeChartClick}
                                                    />
                                                ))}
                                            </tbody>
                                        </SortableContext>
                                    </table>
                                </DndContext>
                            </div>
                            <div className="table-footer">
                                <div className="footer-left">
                                    <span>
                                        Showing {totalEntries > 0 ? startIdx + 1 : 0} to {endIdx} of {totalEntries} entries • Page {page} of {totalPages}
                                        {isUpdatingPositions && <span className="position-updating"> • Updating positions...</span>}
                                    </span>
                                </div>
                                <div className="pagination">
                                    <button
                                        type="button"
                                        disabled={page === 1}
                                        onClick={() => handlePageChange(page - 1)}
                                        aria-label="Previous page"
                                    >
                                        Previous
                                    </button>
                                    {getPageItems().map((item, idx) => (
                                        typeof item === 'number' ? (
                                            <button
                                                type="button"
                                                key={`p-${item}`}
                                                className={page === item ? 'active' : ''}
                                                onClick={() => handlePageChange(item)}
                                                aria-current={page === item ? 'page' : undefined}
                                            >
                                                {item}
                                            </button>
                                        ) : (
                                            <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                                        )
                                    ))}
                                    <button
                                        type="button"
                                        disabled={page === totalPages}
                                        onClick={() => handlePageChange(page + 1)}
                                        aria-label="Next page"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}


                    <ConfirmationModal
                        visible={showDeleteModal}
                        onConfirm={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                        title="Confirm Deletion"
                        message={`Are you sure you want to delete the Category "${categoryToDelete?.name || ''}"?`}
                        confirmText="Delete"
                    />

                    <SizeChartModal
                        show={showSizeChartModal}
                        sizeChart={selectedSizeChart}
                        onClose={handleCloseSizeChartModal}
                    />
                </div>
            </div>
        </>
    );
};

export default Categories; 